import { FieldProps } from "@/core/components/Field";
import { alpha, Box, Theme } from "@mui/system";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { Alert, Button, Typography } from "@mui/material";
import Image from "next/image";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import {
  DocumentProperties,
  FileUploadData,
  HeadingLevel,
  LegalArea,
} from "@/core/types/types";
import {
  MouseEventHandler,
  SyntheticEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { camundaApi } from "@/core/api/camundaApi";
import { FileUploadSuccessResponse } from "@/pages/api/uploadFiles";
import CircularProgress from "@mui/material/CircularProgress";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";
import { FileUpload as ReusableFileUpload } from "@flightright/reusable-components";
import Cookies from "js-cookie";
import { ConfigContext } from "@/core/contexts/ConfigContext";
import getBrandConfiguration from "@/core/utils/getBrandConfiguration";

const ALLOWED_FILE_EXTENSIONS: string[] = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx format
  "image/png",
  "image/jpeg",
  "image/jp2",
  "application/msword",
];

interface FileUploadProps {
  fieldProps: FieldProps;
  uploadFiles: (files: File[]) => Promise<any>;
}

type FileFieldValues = {
  [key: string]: Array<FileUploadData>;
};

type FormValues = FileFieldValues & {
  documentType?: string;
  documentSubtype?: string;
};

enum ErrorTypes {
  maxFileSize = "maxFileSize",
  maxTotalFilesSize = "maxTotalFilesSize",
  maxFilesNumber = "maxFilesNumber",
  networkError = "networkError",
  unknownError = "unknownError",
  filesAreNotUploaded = "filesAreNotUploaded",
  requiredFiles = "requiredFiles",
  invalidFileType = "invalidFileType",
  brokenFile = "brokenFile",
}

type ErrorMessages = {
  [key in keyof typeof ErrorTypes as string]: string;
};

type FormProps = {
  currentFilesNumber: number;
  currentTotalFilesSize: number;
};

const styles = (theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.grey.A100}`,
    borderRadius: "5px",
  },
  ticketIcon: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "45px",
    marginTop: theme.spacing(2),
  },
  uploadButton: {
    display: "flex",
    marginRight: "4px",
    "& > .MuiButtonBase-root": {
      padding: theme.spacing(0, 2),
    },
    height: "40px",
  },
  filesContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  fileLabel: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.background.paper,
    padding: "5px 8px",
    borderRadius: "15px",
  },
  fileIcon: {
    marginLeft: "2px",
    cursor: "pointer",
    color: alpha(theme.palette.background.paper, 0.7),
    position: "relative",
    left: "5px",
  },
});

const MAX_FILE_SIZE: number = 5 * 1024 * 1024; // 5MB
const MAX_TOTAL_FILES_SIZE: number = 10 * 1024 * 1024; // 10MB
const MAX_ALLOWED_FILES_NUMBER: number = 5;

const ButtonIcon = ({
  loading,
  onClick,
}: {
  loading: boolean;
  onClick: MouseEventHandler;
}) => {
  const classes: any = useCustomStyles(styles);

  return loading ? (
    <CircularProgress color="inherit" size="20px" sx={classes?.fileIcon} />
  ) : (
    <CancelRoundedIcon
      fontSize="small"
      onClick={onClick}
      sx={classes?.fileIcon}
    />
  );
};

const Warning = ({ message }: { message: string }) => {
  return (
    <Box mt={2}>
      <Alert severity="warning" variant="filled">
        <Typography variant={HeadingLevel.Body2}>{message}</Typography>
      </Alert>
    </Box>
  );
};

const FileUpload = ({
  fieldProps: { field },
  uploadFiles,
}: FileUploadProps) => {
  if (!uploadFiles) {
    throw new Error("Missing required uploadFiles function.");
  }

  const initializationConfig = useContext(ConfigContext);
  const brandConfig = getBrandConfiguration(initializationConfig.brandId);

  const legalArea = Cookies.get("legalArea") || LegalArea.DEFAULT.toLowerCase();
  const {
    version,
    [DocumentProperties.documentType]: documentType,
    [DocumentProperties.documentSubtype]: documentSubtype,
  } = field.properties;
  const isCompact: boolean = version === "compact";

  const getMaxFileSize = (legalArea: string) => {
    if (legalArea === LegalArea.MIE) {
      return 20 * 1024 * 1024; // 20 MB
    }
    return MAX_FILE_SIZE;
  };

  const getMaxTotalFileSize = (legalArea: string) => {
    if (legalArea === LegalArea.MIE) {
      return 50 * 1024 * 1024; // 50 MB
    }
    return MAX_TOTAL_FILES_SIZE;
  };

  const classes: any = useCustomStyles(styles);
  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
  } = useFormContext<FormValues>();

  const {
    fields: files,
    append,
    remove,
    update,
  } = useFieldArray({
    control,
    name: field.key,
  });

  const [userIsOffline, setUserIsOffline] = useState(false);
  const documentInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    documentType && setValue("documentType", documentType);
    documentSubtype && setValue("documentSubtype", documentSubtype);
  }, [documentType, documentSubtype]);

  const errorMessages: ErrorMessages = {
    [ErrorTypes.maxFileSize]: t(`validate.${field.key}.maxFileSize.message`),
    [ErrorTypes.maxTotalFilesSize]: t(
      `validate.${field.key}.maxTotalFilesSize.message`
    ),
    [ErrorTypes.maxFilesNumber]: t(
      `validate.${field.key}.maxFilesNumber.message`
    ),
    [ErrorTypes.networkError]: t(`validate.${field.key}.networkError.message`),
    [ErrorTypes.unknownError]: t(`validate.${field.key}.unknownError.message`),
    [ErrorTypes.invalidFileType]: t(
      `validate.${field.key}.invalidFileType.message`
    ),
    [ErrorTypes.filesAreNotUploaded]: t(
      `validate.${field.key}.filesAreNotUploaded.message`
    ),
    [ErrorTypes.requiredFiles]: t(`validate.${field.key}.required.message`),
    [ErrorTypes.brokenFile]: t(`validate.${field.key}.brokenFile.message`),
  };

  const filesUploaded: boolean = files.length !== 0;
  const formStateError: boolean = !!errors[field.key];
  const formStateErrorMessage: string =
    errors[field.key]?.message || errors[field.key]?.root?.message || "";

  const fileUploadIcon =
    brandConfig.fileUploadLogoSource ||
    `images/icons/file-upload/booking-confirmation${
      filesUploaded ? "-filled" : ""
    }.svg`;

  const handleMultipleFilesUpload = async (event: SyntheticEvent) => {
    clearErrors(field.key);
    const target = event.target as HTMLInputElement;

    let formProps: FormProps = {
      currentFilesNumber: files.length,
      currentTotalFilesSize: files.reduce((acc, doc) => acc + doc.fileSize, 0),
    };
    const filesToUpload: File[] = [];
    for (const file of Array.from(target?.files || [])) {
      if (!file) {
        continue;
      }

      const fileIsValid = await validateFile(file, formProps);
      if (!fileIsValid) {
        continue;
      }

      formProps.currentTotalFilesSize += file.size;
      formProps.currentFilesNumber += 1;

      filesToUpload.push(file);
      append({
        uuid: null,
        fileSize: file.size,
        fileName: file.name,
        loading: true,
        mimeType: file.type,
      } as FileUploadData);
    }

    await updateUploadedFiles(filesToUpload);
  };

  const updateUploadedFiles = async (uploadedFiles: File[]) => {
    if (uploadedFiles.length === 0) return;

    const newFilesIndexes = Array.from(
      { length: uploadedFiles.length },
      (_, index) => files.length + index
    );
    let response;
    try {
      response = await uploadFiles(uploadedFiles);
      clearErrors(field.key);
    } catch (err) {
      setFormError(ErrorTypes.unknownError);
      remove(newFilesIndexes);
      return;
    }

    const uuidPerFileName = response.reduce(
      (acc: any, file: FileUploadSuccessResponse) => {
        acc[file.filename] = file.uuid;
        return acc;
      },
      {}
    );

    newFilesIndexes.forEach((index) => {
      const fileData: File = uploadedFiles[index - newFilesIndexes[0]];
      const updatedFile = {
        uuid: uuidPerFileName[fileData.name],
        fileName: fileData.name,
        fileSize: fileData.size,
        loading: false,
        mimeType: fileData.type,
      };
      update(index, updatedFile);
    });
  };

  const validateFile = async (file: File, formProps: FormProps) => {
    if (!ALLOWED_FILE_EXTENSIONS.includes(file.type)) {
      setFormError(ErrorTypes.invalidFileType);
      return false;
    }
    if (userIsOffline) {
      setFormError(ErrorTypes.networkError);
      return false;
    }
    const fileSize = file.size;
    if (fileSize > getMaxFileSize(legalArea)) {
      setFormError(ErrorTypes.maxFileSize);
      return false;
    }

    if (formProps.currentFilesNumber >= MAX_ALLOWED_FILES_NUMBER) {
      setFormError(ErrorTypes.maxFilesNumber);
      return false;
    }
    if (
      formProps.currentTotalFilesSize + fileSize >=
      getMaxTotalFileSize(legalArea)
    ) {
      setFormError(ErrorTypes.maxTotalFilesSize);
      return false;
    }

    if (file.type === "application/pdf" && (await isPdfFileCorrupted(file))) {
      setFormError(ErrorTypes.brokenFile);
      return false;
    } else if (
      file.type.startsWith("image/") &&
      (await isImageFileCorrupted(file))
    ) {
      setFormError(ErrorTypes.brokenFile);
      return false;
    }

    return true;
  };

  const isPdfFileCorrupted = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onerror = () => {
        resolve(true);
      };

      reader.onload = () => {
        const buffer = reader.result as ArrayBuffer;

        const headerText = new TextDecoder().decode(buffer.slice(0, 5));
        if (!headerText.includes("%PDF-")) {
          return resolve(true);
        }

        const trailerText = new TextDecoder().decode(buffer.slice(-6));
        if (!trailerText.includes("%%EOF")) {
          return resolve(true);
        }

        resolve(false);
      };

      reader.readAsArrayBuffer(file);
    });
  };

  const isImageFileCorrupted = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new window.Image();

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(false);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(true);
      };

      img.src = url;
    });
  };

  const setFormError = (errorType: ErrorTypes) => {
    setError(field.key, {
      type: errorType,
      message: errorMessages[errorType],
    });
  };

  const handleRemove = (index: number) => {
    remove(index);

    const formIsValid = formStateIsValid();
    if (formIsValid && formStateError) {
      clearErrors(field.key);
    }
    // clearing input cache, so the same file can be uploaded again if it was removed before
    if (documentInputRef.current) {
      documentInputRef.current.value = "";
    }
  };

  const formStateIsValid = () => {
    const totalFilesSize = files.reduce((acc, doc) => acc + doc.fileSize, 0);
    if (files.length > MAX_ALLOWED_FILES_NUMBER) {
      setFormError(ErrorTypes.maxFilesNumber);
      return false;
    } else if (totalFilesSize >= getMaxTotalFileSize(legalArea)) {
      setFormError(ErrorTypes.maxTotalFilesSize);
      return false;
    }

    return true;
  };

  // tracking user network status to show corresponding warning
  // shown on file upload attempt, and disappears when the issue is resolved
  useEffect(() => {
    const handleOfflineStatus = () => {
      setUserIsOffline(!navigator.onLine);
    };
    const handleOnlineStatus = () => {
      setUserIsOffline(navigator.onLine);
      clearErrors(field.key);
    };

    window.addEventListener("offline", handleOfflineStatus);
    window.addEventListener("online", handleOnlineStatus);

    return () => {
      window.removeEventListener("offline", handleOfflineStatus);
      window.removeEventListener("online", handleOnlineStatus);
    };
  }, []);

  return (
    <Box id={field.key} sx={!isCompact && classes?.root}>
      {!isCompact && (
        <Box sx={classes?.ticketIcon}>
          <Image
            width="85"
            height="34"
            src={fileUploadIcon}
            alt="booking confirmation"
          />
        </Box>
      )}
      <Box sx={classes?.filesContainer}>
        <Box sx={classes?.uploadButton}>
          <Button variant="outlined" component="label">
            <UploadFileIcon />
            <Typography
              variant={HeadingLevel.Body2}
              sx={{ textTransform: "none" }}
              ml={1}
            >
              {t("boardingPass.label")}
            </Typography>
            <Controller
              key={field.key}
              name={field.key}
              control={control}
              rules={{
                validate: (values: FileUploadData[] = []) => {
                  if (field.validate?.required && values.length === 0) {
                    return errorMessages[ErrorTypes.requiredFiles];
                  }
                  const allFilesAreUploaded = values?.every(
                    (file) => !!file?.uuid
                  );
                  const message = errorMessages[ErrorTypes.filesAreNotUploaded];
                  return allFilesAreUploaded || message;
                },
              }}
              render={() => (
                <ReusableFileUpload
                  id="file-input"
                  name={field.key}
                  control={control}
                  documentInputRef={documentInputRef}
                  handleChange={handleMultipleFilesUpload}
                />
              )}
            />
          </Button>
        </Box>
        {files.length === 0 && (
          <Typography variant={HeadingLevel.Body2}>
            {t("boardingPass.empty.label")}
          </Typography>
        )}
        {files.map((file, index) => (
          <Box key={file.id} sx={classes?.fileLabel}>
            <Typography variant={HeadingLevel.Body2}>
              {file.fileName}
            </Typography>
            <ButtonIcon
              loading={file.loading}
              onClick={() => handleRemove(index)}
            />
          </Box>
        ))}
      </Box>
      {formStateError && <Warning message={formStateErrorMessage} />}
    </Box>
  );
};

const FileUploadWithCamunda = (fieldProps: FieldProps) => {
  const uploadFilesFunction = camundaApi.uploadFiles;

  return (
    <FileUpload fieldProps={fieldProps} uploadFiles={uploadFilesFunction} />
  );
};

export { FileUpload, FileUploadWithCamunda };
